import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { mapToOptions, Option } from "@sinch/utils/option";
import { RequirementsData } from "@sinch/views/ShiftTimeline/StaffFilter/queries.graphql";
import { head, isNil } from "ramda";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RequirementsDataQuery } from "@sinch/components/application/Requirements/queries.types";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

type RequirementSubjectSelectProps<T extends AutocompleteOptions = AutocompleteOptions> = Omit<
  AutocompleteInputProps<T, false, true, false>,
  "disableClearable" | "inputReadOnly" | "options" | "multiple" | "freeSolo" | "ref"
> & {
  requirementId: string | number | undefined;
};

export const RequirementSubjectInput = <T extends AutocompleteOptions = AutocompleteOptions>({
  requirementId,
  ...props
}: RequirementSubjectSelectProps<T>) => {
  const { t } = useTranslation();

  const { data, loading } = useQueryHandler<RequirementsDataQuery>(RequirementsData);
  const subjects = data?.requirements?.find((r) => r.id === requirementId)?.subjects;

  const options: Option[] = useMemo(() => (subjects ? mapToOptions("subject", "label")(subjects) : []), [subjects]);

  useEffect(() => {
    if (!loading) {
      props.onChange?.(props.value ? props.value : (head(options)?.value ?? null));
    }
  }, [loading, options, props.value]);

  return (
    <AutocompleteInput
      disablePortal={false}
      label={t("Requirement.type")}
      placeholder={t("Requirement.enterRequirementSubject")}
      size="small"
      {...props}
      disableClearable
      disabled={isNil(requirementId)}
      freeSolo={false}
      inputReadOnly
      multiple={false}
      options={options}
    />
  );
};
